import { useQuery } from '@tanstack/react-query';
import { CacheConfig } from './consts';
import authFetch from './fetch';
import { getQueryParams, makeRequest } from './utils';

const ORGANIZATIONS_PATH = '/api/Organizations';

export const getOrganization = (id) => makeRequest(authFetch.get(`${ORGANIZATIONS_PATH}/${id}`));
export const useGetOrganizationById = (id) => useQuery([`${ORGANIZATIONS_PATH}/${id}`], () => getOrganization(id), { enabled: !!id });

export const getOrganizationsLookupsUrl = (params) => `/api/Organizations/Lookup${getQueryParams(params)}`;
export const getOrganizationsLookup = (params) => makeRequest(authFetch.get(getOrganizationsLookupsUrl(params))).then((data) => data.map((x) => ({ ...x, label: x.scac ?? x.name, value: x.id })));
export const useGetOrganizationsLookup = (params, opts) => useQuery([getOrganizationsLookupsUrl(params)], () => getOrganizationsLookup(params), { ...CacheConfig.forever, ...opts });

export const getSSLScacLookup = (name) => makeRequest(authFetch.get(`/api/ShipLines/lookup/name/${name}`));
export const getSSLLookup = (name) => makeRequest(authFetch.get('/api/ShipLines/lookup'));

export const getOrganizationsGrid = (params) => makeRequest(authFetch.get(`/api/Organizations/Grid${getQueryParams(params)}`));

export const getOrganizationsGridWithSubsidaries = (params) => makeRequest(authFetch.get(`/api/Organizations/GridWithSubsidaries${getQueryParams(params)}`));

export const getv1OrganizationsGrid = (params) => makeRequest(authFetch.get(`/api/Organizations/v1/Grid${getQueryParams(params)}`));

export const postOrganization = (organization) => makeRequest(authFetch.put('/api/Organizations/upsert', organization));

export const deleteOrganization = (id) => makeRequest(authFetch.delete(`${ORGANIZATIONS_PATH}/${id}`));

export const restoreOrganization = (id) => makeRequest(authFetch.patch(`/api/Organizations/${id}/restore`));

export const getOrganizationOrders = (id) => makeRequest(authFetch.get(`/api/Organizations/Orders?OrganizationId=${id}`));

export const getOrganizationOrdersCount = (id) => makeRequest(authFetch.get(`/api/Organizations/${id}/Orders/Count`));

export const getOrganizationContacts = (id, terminalId) => {
  const url = terminalId
    ? `/api/Organizations/${id}/Contacts?terminalId=${terminalId}`
    : `/api/Organizations/${id}/Contacts`;
  return makeRequest(authFetch.get(url));
};

export const getOrganizationContactsForOrganizations = (ids) => makeRequest(
  authFetch.get(
    `/api/Organizations/Contacts?${ids
      .filter((x) => x)
      .map((id) => `ids=${id}&`)
      .join('')}`,
  ),
);

export const getOrganizationContact = (orgId, id) => makeRequest(authFetch.get(`/api/Organizations/${orgId}/Contacts/${id}`));

export const saveOrganizationContact = (id, contact) => makeRequest(authFetch.patch(`/api/Organizations/${id}/Contacts`, contact));

export const removeOrganizationContact = (id, contactId) => makeRequest(authFetch.delete(`/api/Organizations/${id}/Contacts/${contactId}`));

export const upsertOrganizationContactEmail = (organizationId, contactId, email) => makeRequest(authFetch.patch(`/api/Organizations/${organizationId}/Contacts/${contactId}/Email`, email));
export const upsertOrganizationContactPhone = (organizationId, contactId, phone) => makeRequest(authFetch.patch(`/api/Organizations/${organizationId}/Contacts/${contactId}/Phone`, phone));
export const getOrganizationRoles = () => makeRequest(authFetch.get('/api/Organizations/Roles'));
export const getOrganizationCustomerTypes = () => makeRequest(authFetch.get('/api/Organizations/OrganizationCustomerTypes'));

export const getAccountStatuses = () => makeRequest(authFetch.get('/api/Organizations/AccountStatuses')).then((data) => data.map((x) => ({ ...x, keyStatusValue: x.value, keyStatusName: x.description })));
export const useGetAccountStatuses = () => useQuery(['/api/Organizations/AccountStatuses'], () => getAccountStatuses(), CacheConfig.forever);

export const getAccountingStatuses = () => makeRequest(authFetch.get('/api/Organizations/AccountingStatuses'));

export const getOrganizationsTotalsByStatus = () => makeRequest(authFetch.get('/api/Organizations/TotalsByStatus'));

export const getSendingInvoiceFormats = () => makeRequest(authFetch.get('/api/Organizations/SendingInvoiceFormats'));

export const getInvoiceBatchingOptions = () => makeRequest(authFetch.get('/api/Organizations/InvoiceBatchingOptions'));

export const getInvoiceDeliveryOptions = () => makeRequest(authFetch.get('/api/Organizations/InvoiceDeliveryOptions'));
export const getInvoiceBillingFrequencies = () => makeRequest(authFetch.get('/api/Organizations/InvoiceBillingFrequencies'));

export const getFuelIndexTypes = () => makeRequest(authFetch.get('/api/Organizations/FuelIndexTypes'));

export const getInvoiceReferenceNumberTypes = () => makeRequest(authFetch.get('/api/Organizations/InvoiceReferenceNumberType'));

export const validateOrganizationDelete = (id) => makeRequest(authFetch.get(`/api/Organizations/ValidateDelete?OrganizationId=${id}`));

export const getOrganizationAudits = (id, params) => makeRequest(authFetch.get(`/api/Organizations/${id}/Audits${getQueryParams(params)}`));

export const getOrganizationAuditProperties = (id) => makeRequest(authFetch.get(`/api/Organizations/${id}/Audits/Properties`));

export const getOrganizationAuditUsers = (id) => makeRequest(authFetch.get(`/api/Organizations/${id}/Audits/Users`));

export const getOrganizationAdditionalChargesAudits = (id, params) => makeRequest(authFetch.get(`/api/Organizations/AdditionalCharges/${id}/Audits${getQueryParams(params)}`));

export const getOrganizationAdditionalChargesAuditProperties = (id) => makeRequest(authFetch.get(`/api/Organizations/AdditionalCharges/${id}/Audits/Properties`));

export const getOrganizationAdditionalChargesAuditUsers = (id) => makeRequest(authFetch.get(`/api/Organizations/AdditionalCharges/${id}/Audits/Users`));

export const getOrganisationFileTypes = () => makeRequest(authFetch.get('api/Organizations/DocumentTypes'));

export const getOrganizationFiles = (id, params) => makeRequest(
  authFetch.get(`api/Organizations/${id}/Files${getQueryParams(params)}`),
);

export const postOrganizationFiles = (id, files) => makeRequest(authFetch.post(`api/Organizations/${id}/Files`, files));

export const deleteOrganizationFile = (id, fileId) => makeRequest(authFetch.delete(`api/Organizations/${id}/Files/${fileId}`));

export const getOrganizationLocationVolumes = (id) => makeRequest(authFetch.get(`/api/Organizations/${id}/LocationVolumes`));

export const getQuotesByOrganization = (id) => makeRequest(authFetch.get(`/api/Organizations/${id}/Quotes`));

export const getOrganizationWebsites = (id) => makeRequest(authFetch.get(`/api/Organizations/${id}/Websites`));

export const saveOrganizationWebsite = (id, website) => makeRequest(authFetch.patch(`/api/Organizations/${id}/Websites`, website));

export const removeOrganizationWebsite = (id, websiteId) => makeRequest(authFetch.delete(`/api/Organizations/${id}/Websites/${websiteId}`));

export const getOrganizationAdditionalCharges = (id, params) => makeRequest(authFetch.get(`/api/Organizations/${id}/AdditionalCharges${getQueryParams(params)}`));

export const saveOrganizationAdditionalCharge = (id, additionalCharge) => makeRequest(authFetch.patch(`/api/Organizations/${id}/AdditionalCharges`, additionalCharge));

export const removeOrganizationAdditionalCharge = (id, additionalChargeId) => makeRequest(authFetch.delete(`/api/Organizations/${id}/AdditionalCharges/${additionalChargeId}`));

export const getOrganizationTariffs = (id) => makeRequest(authFetch.get(`/api/Organizations/${id}/Tariffs`));

export const saveOrganizationTariff = (id, tariff) => makeRequest(authFetch.patch(`/api/Organizations/${id}/Tariffs`, tariff));

export const getOrganizationPreferredPools = (id) => makeRequest(authFetch.get(`/api/Organizations/${id}/PreferredPools`));

export const saveOrganizationPreferredPool = (id, preferredPool) => makeRequest(authFetch.patch(`/api/Organizations/${id}/PreferredPools`, preferredPool));

export const getFuelSurchargeCalculationTypes = () => makeRequest(authFetch.get('/api/Quotes/FuelSurchargeCalculationTypes'));

export const getOrganizationRailbillings = (organizationId, params) => makeRequest(authFetch.get(`/api/OrganizationsRailbilling${getQueryParams(params)}&organizationId=${organizationId}`));

export const getOrganizationRailbilling = (id) => makeRequest(authFetch.get(`/api/OrganizationsRailbilling/${id}`));

export const deleteOrganizationRailbilling = (id) => makeRequest(authFetch.delete(`/api/OrganizationsRailbilling/${id}`));

export const upsertOrganizationRailbilling = (railbilling) => makeRequest(authFetch.put('/api/OrganizationsRailbilling/', railbilling));

export const getOrganizationRailPorts = (id) => makeRequest(authFetch.get(`/api/Organizations/${id}/RailPorts`));

export const upsertOrganizationRailPort = (id, railPort) => makeRequest(authFetch.patch(`/api/Organizations/${id}/RailPort`, railPort));

export const deleteOrganizationRailPort = (organizationId, railPortId) => makeRequest(authFetch.delete(`/api/Organizations/${organizationId}/RailPort/${railPortId}`));

export const getRailbillingMethods = () => makeRequest(authFetch.get('/api/OrganizationsRailbilling/methods'));
export const compareOrganizations = (v1, v2) => makeRequest(authFetch.post('/api/Organizations/Compare', { v1, v2 }));
export const updateOrganizationField = (query) => makeRequest(authFetch.post('/api/Organizations/UpdateField', query));
export const ignoreOrganizationField = (query) => makeRequest(authFetch.post('/api/Organizations/IgnoreField', query));

export const getLegacyDetails = (orgId) => makeRequest(authFetch.get(`/api/Organizations/${orgId}/LegacyDetails`));
export const addLegacyDetails = (details, id, updateRoles) => makeRequest(
  authFetch.post('/api/Organizations/AddLegacyDetails', {
    details,
    organizationId: id,
    updateRoles,
  }),
);
export const gev1Types = () => makeRequest(authFetch.get('/api/Organizations/v1OrganizationTypes'));

export const getOrganizationNotes = (id, params) => makeRequest(authFetch.get(`/api/Organizations/${id}/notes${getQueryParams(params)}`));
export const patchOrganizationNote = (id, note) => makeRequest(authFetch.patch(`/api/Organizations/${id}/notes`, note));

export const getSubsidiaries = (id, params) => makeRequest(authFetch.get(`/api/Organizations/${id}/subsidiaries${getQueryParams(params)}`));
export const getSubsidiary = (id, subsidiaryId) => makeRequest(authFetch.get(`/api/Organizations/${id}/subsidiary/${subsidiaryId}`));
export const upsertSubsidiary = (id, subsidiary) => makeRequest(authFetch.put(`/api/Organizations/${id}/subsidiary`, subsidiary));
export const deleteSubsidiary = (id, subsidiaryId) => makeRequest(authFetch.delete(`/api/Organizations/${id}/subsidiary/${subsidiaryId}`));
export const getSubsidiaryQuotes = (id, subsidiaryId) => makeRequest(authFetch.get(`/api/Organizations/${id}/subsidiary/${subsidiaryId}/quotes`));
export const withSameName = (name) => makeRequest(authFetch.get(`/api/Organizations/SameName/${encodeURIComponent(name)}`));
export const findSimilarOrganizations = (organizationName) => makeRequest(authFetch.get(`/api/Organizations/Search${getQueryParams({ organizationName })}`));
export const unlinkLegacyOrganization = (organizationId, legacyId) => makeRequest(authFetch.delete(`/api/Organizations/${organizationId}/LegacyLink/${legacyId}`));
export const getOrganizationsBulkUpdateProperties = () => makeRequest(authFetch.get('/api/Organizations/BulkUpdate/Properties'));
export const getOrganizationTariffsUpdateFrequencies = () => makeRequest(authFetch.get('/api/Organizations/OrganizationTariffsUpdateFrequencies'));
export const bulkUpdateOrganizations = (request) => makeRequest(authFetch.patch('/api/Organizations/BulkUpdate', request));

export const getOffices = (params) => makeRequest(authFetch.get(`/api/OrganizationsOffices${getQueryParams(params)}`));
export const getOfficesLookups = (params) => makeRequest(authFetch.get(`/api/OrganizationsOffices/Lookups${getQueryParams(params)}`));
export const getOffice = (id) => makeRequest(authFetch.get(`/api/OrganizationsOffices/${id}`));
export const upsertOffice = (office) => makeRequest(authFetch.put('/api/OrganizationsOffices', office));
export const deleteOffice = (id) => makeRequest(authFetch.delete(`/api/OrganizationsOffices/${id}`));
export const updateLegacyOrganizationPrimaries = (id, data) => makeRequest(authFetch.put(`/api/Organizations/${id}/LegacyOrganizationPrimaries`, data));
export const checkOrganizationHasLegacyLink = (id, role) => makeRequest(authFetch.get(`/api/Organizations/CheckOrganizationHasLegacyLink/${id}/ForRole/${role}`));
export const getSteamshipLineInfo = (params) => makeRequest(authFetch.get(`/api/Organizations/SteamshipLineInfo${getQueryParams(params)}`));

export const getImpactedQuoteLanes = (additionalCharge) => makeRequest(authFetch.put('/api/Organizations/GetImpactedQuoteLanes', additionalCharge));

export const getOrgQuoteLaneSettings = (orgId, params) => makeRequest(authFetch.get(`/api/Organizations/${orgId}/QuoteLaneSettings${getQueryParams(params)}`));
export const upsertOrgQuoteLaneSettings = (orgId, data) => makeRequest(authFetch.patch(`/api/Organizations/${orgId}/QuoteLaneSettings`, data));